import {
  ListButton,
  Show,
  TagField,
  TextField,
} from "@refinedev/antd";

import { Divider, Space, Table, Tag, Typography } from "antd";

import {
  IResourceComponentsProps,
  useShow,
} from "@refinedev/core";

import { IVesselTransfer, IVesselTransferItem } from "interfaces";
import React from "react";
import { getStatusTagColor } from "constants/tagColors";
import { ShowSection, ShowField, ShowFieldGrid } from "components/show";
import { SwapOutlined, UnorderedListOutlined } from "@ant-design/icons";

const { Text } = Typography;

const TRANSFER_TYPE_LABELS: Record<string, string> = {
  to_old_vessels: "Deactivate (to Deactivated Vessels)",
  between_shipcos: "Transfer (between Shipcos)",
  from_old_vessels: "Reactivate (from Deactivated Vessels)",
};

const TRANSFER_TYPE_COLORS: Record<string, string> = {
  to_old_vessels: "volcano",
  between_shipcos: "geekblue",
  from_old_vessels: "cyan",
};

export const VesselTransferShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IVesselTransfer>({
    meta: {
      join: [
        { field: "sourceShipco" },
        { field: "targetShipco" },
        { field: "items" },
      ],
    },
  });
  const record = result;

  const items = record?.items || [];

  return (
    <Show
      breadcrumb={false}
      isLoading={isLoading}
      headerProps={{
        title: "Vessel Transfer Details",
        onBack: () => window.history.back(),
        subTitle: "transfer details and vessel items",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        ghost: true,
        extra: (
          <React.Fragment>
            <Space>
              <ListButton resource="vessel-transfer">All Transfers</ListButton>
            </Space>
          </React.Fragment>
        ),
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        <ShowSection
          title="Transfer Information"
          subtitle="Transfer type, shipcos and metadata"
          icon={<SwapOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField
              label="Transfer Type"
              value={
                record?.transferType
                  ? TRANSFER_TYPE_LABELS[record.transferType] || record.transferType
                  : undefined
              }
              type="tag"
              tagColor={TRANSFER_TYPE_COLORS[record?.transferType || ""] || "blue"}
            />
            <ShowField
              label="Status"
              value={record?.status}
              type="status"
              tagColor={getStatusTagColor(record?.status || "")}
            />
            <ShowField
              label="Source Shipco"
              value={record?.sourceShipco?.registrationName}
            />
            <ShowField
              label="Target Shipco"
              value={record?.targetShipco?.registrationName}
            />
            <ShowField
              label="Performed By"
              value={record?.performedBy}
            />
            <ShowField
              label="Created At"
              value={record?.createdAt}
              type="date"
              dateFormat="DD-MMM-YYYY HH:mm"
            />
            <ShowField
              label="Effective Date"
              value={record?.effectiveDate}
              type="date"
            />
            <ShowField
              label="Reason"
              value={record?.reason}
              type="notes"
            />
          </ShowFieldGrid>
        </ShowSection>

        <ShowSection
          title="Transfer Items"
          subtitle=""
          icon={<UnorderedListOutlined />}
          variant="elevated"
        >
          <Table
            dataSource={items}
            rowKey="id"
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total}`,
            }}
          >
            <Table.Column
              title="IMO"
              width={100}
              render={(_, record: IVesselTransferItem) => (
                <TextField value={record.imoNumber || record.vessel?.imoNumber || "N/A"} />
              )}
            />
            <Table.Column
              title="Vessel Name"
              width={180}
              render={(_, record: IVesselTransferItem) => (
                <TextField value={record.vessel?.name || "N/A"} />
              )}
            />
            <Table.Column
              title="Previous Shipco"
              width={180}
              render={(_, record: IVesselTransferItem) => (
                <TextField value={record.previousShipco?.registrationName || "N/A"} />
              )}
            />
            <Table.Column
              title="Previous Group"
              width={150}
              render={(_, record: IVesselTransferItem) => (
                <TextField value={record.previousShipcoGroup?.registrationName || "N/A"} />
              )}
            />
            <Table.Column
              dataIndex="previousHoldStatus"
              title="Prev Hold Status"
              width={130}
              render={(value) =>
                value ? (
                  <TagField
                    color={getStatusTagColor(value)}
                    value={value}
                  />
                ) : (
                  <Text>N/A</Text>
                )
              }
            />
            <Table.Column
              dataIndex="previousVesselName"
              title="Previous Name"
              width={150}
              render={(value) => <TextField value={value || "—"} />}
            />
            <Table.Column
              dataIndex="newVesselName"
              title="New Name"
              width={150}
              render={(value) => <TextField value={value || "—"} />}
            />
          </Table>
        </ShowSection>
      </Space>
    </Show>
  );
};
